/**
 * PATH: apps/qa/src/pages/template-detail/SortableSection.tsx
 * PURPOSE: Draggable template section with nested sortable items
 */

import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core'
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Trash2, ChevronDown, ChevronRight, Plus } from 'lucide-react'
import type { TemplateSection, TemplateItem } from './types'
import { getSectionId, getItemId } from './types'
import { SortableItem } from './SortableItem'

interface SortableSectionProps {
  section: TemplateSection
  sectionIndex: number
  isExpanded: boolean
  onToggle: () => void
  onUpdate: (updates: Partial<TemplateSection>) => void
  onRemove: () => void
  onAddItem: () => void
  onUpdateItem: (itemIndex: number, updates: Partial<TemplateItem>) => void
  onRemoveItem: (itemIndex: number) => void
  onReorderItems: (oldIndex: number, newIndex: number) => void
}

export function SortableSection({
  section, sectionIndex, isExpanded, onToggle, onUpdate, onRemove,
  onAddItem, onUpdateItem, onRemoveItem, onReorderItems,
}: SortableSectionProps) {
  const id = getSectionId(section, sectionIndex)
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }

  const itemIds = section.items.map((item, idx) => getItemId(item, sectionIndex, idx))
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleItemDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const oldIndex = itemIds.indexOf(String(active.id))
      const newIndex = itemIds.indexOf(String(over.id))
      if (oldIndex !== -1 && newIndex !== -1) onReorderItems(oldIndex, newIndex)
    }
  }

  return (
    <div ref={setNodeRef} style={style} className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
      <div className="flex items-center gap-3 p-4 border-b border-border bg-muted/30">
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing touch-none"><GripVertical className="w-5 h-5 text-muted-foreground" /></div>
        <button onClick={onToggle} className="p-1 rounded hover:bg-accent transition-colors">
          {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
        </button>
        <input type="text" value={section.title} onChange={(e) => onUpdate({ title: e.target.value })}
          className="flex-1 bg-transparent font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 px-2 py-1 rounded" />
        <span className="text-sm text-muted-foreground">{section.items.length} items</span>
        <button onClick={onRemove} className="p-1.5 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
      </div>
      {isExpanded && (
        <div className="p-4 space-y-3">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleItemDragEnd}>
            <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
              {section.items.map((item, itemIndex) => (
                <SortableItem key={getItemId(item, sectionIndex, itemIndex)} item={item} itemIndex={itemIndex} sectionIndex={sectionIndex}
                  onUpdate={(updates) => onUpdateItem(itemIndex, updates)} onRemove={() => onRemoveItem(itemIndex)} />
              ))}
            </SortableContext>
          </DndContext>
          <button onClick={onAddItem} className="w-full py-2 rounded-lg border border-dashed border-border text-sm text-muted-foreground hover:text-primary hover:border-primary transition-colors">
            <Plus className="w-4 h-4 inline mr-1" />Add Item
          </button>
        </div>
      )}
    </div>
  )
}
